/**
 * MaoIntroModal - Introduction modal for the AI assistant
 *
 * Shows a one-time welcome modal introducing Mao and its capabilities.
 * Uses localStorage to track if the user has already seen the intro.
 */

import { useState, useEffect } from 'react';
import { Sparkles, Users, Wand2, MessageSquare, X } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { MaoAvatar } from './MaoAvatar';
import { getAssistantName } from '@/lib/mao';
import { cn } from '@/lib/utils';

const STORAGE_KEY = 'mao-intro-seen';

interface MaoIntroModalProps {
  /** Force show the modal (for testing/demo purposes) */
  forceShow?: boolean;
}

export function MaoIntroModal({ forceShow = false }: MaoIntroModalProps) {
  const [open, setOpen] = useState(false);
  const assistantName = getAssistantName();

  useEffect(() => {
    // Check if user has already seen the intro
    if (forceShow) {
      setOpen(true);
      return;
    }

    const hasSeen = localStorage.getItem(STORAGE_KEY);
    if (!hasSeen) {
      // Small delay for better UX - let the page load first
      const timer = setTimeout(() => {
        setOpen(true);
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [forceShow]);

  const handleClose = () => {
    setOpen(false);
    localStorage.setItem(STORAGE_KEY, 'true');
  };

  const features = [
    {
      icon: Users,
      title: 'Import rapid de oaspeți',
      description: 'Lipește o listă de oaspeți și lasă-mă să-i organizez automat.',
      color: 'bg-sage-100 text-sage-600',
    },
    {
      icon: Wand2,
      title: 'Procesare inteligentă',
      description: 'Detectez cupluri, familii și extrag date de contact automat.',
      color: 'bg-lavender-100 text-lavender-600',
    },
    {
      icon: MessageSquare,
      title: 'Verificare și sugestii',
      description: 'Marchez ce nu sunt sigur și îți cer confirmarea înainte.',
      color: 'bg-amber-100 text-amber-600',
    },
  ];

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="sm:max-w-lg p-0 overflow-hidden rounded-3xl">
        {/* Header with gradient */}
        <div className="relative bg-gradient-to-br from-lavender-500 via-lavender-400 to-primary p-8 text-white">
          {/* Close button */}
          <button
            onClick={handleClose}
            className="absolute top-4 right-4 p-2 rounded-full bg-white/20 hover:bg-white/30 transition-colors"
            aria-label="Închide"
          >
            <X className="w-4 h-4" />
          </button>

          {/* Avatar with glow effect */}
          <div className="relative mx-auto w-fit mb-6">
            <div className="absolute inset-0 blur-2xl bg-white/30 rounded-full scale-150" />
            <MaoAvatar size="lg" animated className="relative w-20 h-20" />
          </div>

          <DialogHeader className="text-center space-y-2">
            <DialogTitle className="text-2xl font-serif font-bold">
              Salut! Eu sunt {assistantName}
            </DialogTitle>
            <p className="text-white/90 text-sm">
              Asistentul tău AI pentru organizarea evenimentului
            </p>
          </DialogHeader>
        </div>

        {/* Features */}
        <div className="p-6 space-y-4">
          <p className="text-sm text-muted-foreground text-center">
            Sunt aici să te ajut cu:
          </p>

          <div className="space-y-3">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <div
                  key={index}
                  className="flex items-start gap-4 p-3 rounded-2xl bg-secondary/50 hover:bg-secondary/80 transition-colors"
                >
                  <div
                    className={cn(
                      'w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0',
                      feature.color,
                    )}
                  >
                    <Icon className="w-5 h-5" />
                  </div>
                  <div>
                    <p className="font-medium text-sm">{feature.title}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {feature.description}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>

          {/* CTA */}
          <div className="pt-4 space-y-3">
            <Button
              onClick={handleClose}
              className="w-full h-12 rounded-2xl text-base font-medium gap-2"
            >
              <Sparkles className="w-4 h-4" />
              Hai să începem!
            </Button>
            <p className="text-[10px] text-center text-muted-foreground">
              Mă vei găsi în diferite zone ale platformei, gata să ajut.
            </p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Hook to manually trigger the Mao intro modal
 */
export function useMaoIntro() {
  const reset = () => {
    localStorage.removeItem(STORAGE_KEY);
  };

  const markAsSeen = () => {
    localStorage.setItem(STORAGE_KEY, 'true');
  };

  const hasSeen = () => {
    return localStorage.getItem(STORAGE_KEY) === 'true';
  };

  return { reset, markAsSeen, hasSeen };
}
